import { Field, SchemaContext } from "../schemas";
import { DocumentSearchAndReplace } from "../sync-engine/operations/DocumentSearchAndReplace";
import { AiCodingApi } from "../aicoding/AiCodingApi";
import { GetDocumentBody } from "../sync-engine/operations/GetDocumentBody";
import { syncEngine } from "../sync-engine/SyncEngine";
import { GetContentControlTexts } from "../sync-engine/operations/GetContentControlsTexts";
import { AiCodingProcessor } from "../aicoding/AiCodingProcessor";
import { EnableTrackChanges } from "../sync-engine/operations/EnableTrackChanges";

/**
 * Handles AI-powered document processing and field replacement. This class manages:
 * - Running AI processing on specific document fields or field set.
 * - Extracting document content and content controls
 * - Applying AI-generated content back to the document with track changes enabled
 * - Handling abort/cancel operations for long-running AI requests
 */
export class AiCoding {
  private static currentController: AbortController | null = null;
  private static isRunning: boolean = false;

  static forceQuit() {
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
    this.isRunning = false;
  }

  static async runForField(field: Field, onProgress?: (params: { loading: boolean }) => void) {
    return this.runForFields([field], onProgress);
  }

  static async runForFields(fields: Field[], onProgress?: (params: { loading: boolean }) => void) {
    if (this.isRunning) {
      this.forceQuit();
    }

    this.isRunning = true;
    this.currentController = new AbortController();
    const signal = this.currentController.signal;

    try {
      onProgress?.({ loading: true });

      const mergedSchema = {
        definitions: {},
      };

      const rootRefs = fields.map((field) => {
        const resolvedSchema = SchemaContext.resolveSchemaRecursive(field.node.schema);
        if (resolvedSchema.definitions) {
          mergedSchema.definitions = { ...mergedSchema.definitions, ...resolvedSchema.definitions };
        }

        return resolvedSchema.$ref;
      });

      const definedRootRefs = rootRefs.filter((ref) => ref !== undefined) as string[];

      const bodyText = await syncEngine.perform(new GetDocumentBody());
      const contentControlTexts = await syncEngine.perform(new GetContentControlTexts());
      const processedText = AiCodingProcessor.markAsReplaced(new Set(contentControlTexts), bodyText);
      const processedTextWithNewlines = AiCodingProcessor.escapeNewlines(processedText);

      const aiCodingApi = new AiCodingApi(signal);
      const result = await aiCodingApi.createAndPollAiCoding(mergedSchema, processedTextWithNewlines, definedRootRefs);

      if (signal.aborted) return;

      await syncEngine.perform(new EnableTrackChanges());
      const replacements = AiCodingProcessor.processAiCodingResults(result, fields);
      await syncEngine.perform(new DocumentSearchAndReplace(replacements, signal));
    } catch (error) {
      console.error("Error in AiCoding.runForFields:", error);
    } finally {
      this.isRunning = false;
      onProgress?.({ loading: false });
    }
  }
}
